import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useChat } from "../context/ChatContext";

const keyFor = id => `poehali_push_onboarding_${id}`;
const read = id => {
  try { return JSON.parse(localStorage.getItem(keyFor(id))) || { days: [] }; }
  catch { return { days: [] }; }
};

export const usePushEligibility = () => {
  const { user, setUser } = useAuth();
  const { subscribe } = useChat();
  const [eligible, setEligible] = useState(false);
  const userId = user?.id;
  const isDriver = user?.roles?.includes("driver");
  const milestone = useCallback(reason => {
    if (!userId) return;
    const state = read(userId);
    if (!state.reason) localStorage.setItem(keyFor(userId), JSON.stringify({ ...state, reason }));
    setEligible(true);
  }, [userId]);

  useEffect(() => {
    if (!userId) { setEligible(false); return; }
    const visit = () => {
      if (document.visibilityState === "hidden") return;
      const state = read(userId), now = new Date();
      const day = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
      const days = [...new Set([...(state.days || []), day])].slice(0, 3);
      const reason = state.reason || (state.wasDriver === false && isDriver ? "driver_granted" : days.length >= 3 ? "third_day" : null);
      localStorage.setItem(keyFor(userId), JSON.stringify({ ...state, days, reason, wasDriver: !!isDriver }));
      setEligible(!!reason);
    };
    const storage = e => { if (e.key === keyFor(userId)) setEligible(!!read(userId).reason); };
    visit();
    window.addEventListener("focus", visit); document.addEventListener("visibilitychange", visit); window.addEventListener("storage", storage);
    return () => { window.removeEventListener("focus", visit); document.removeEventListener("visibilitychange", visit); window.removeEventListener("storage", storage); };
  }, [userId, isDriver]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const unsub = subscribe(ev => {
      if (ev.event === "new_message") {
        const m = ev.data.message;
        if (m.type === "order" && m.sender_id === userId) milestone("order_created");
        else if (m.sender_id !== userId && m.chat_id !== "global") milestone("first_dm");
        else if (isDriver && m.chat_id === "global" && m.type === "order") milestone("driver_order");
      } else if (ev.event === "user_roles_updated" || ev.event === "__reconnected") {
        api.get("/me").then(({ data }) => { if (!cancelled && data.id === userId) setUser(data); }).catch(() => {});
      }
    });
    return () => { cancelled = true; unsub(); };
  }, [subscribe, userId, isDriver, milestone, setUser]);
  return { eligible, milestone };
};
